// Articles loader. Three runtime contexts:
//   1. SSR (Pages Function): articles are passed in via setArticles() before
//      render() runs. Query result comes from D1.
//   2. Client (post-hydration in browser): articles are read from
//      window.__INITIAL_DATA__.articles, set by the SSR'd HTML's <script>.
//   3. Vite dev / build prerender of non-insights routes: articles fall back
//      to the historic glob-from-markdown pipeline so local dev still works
//      without a D1 database.
//
// SPA navigation (e.g. user lands on / then clicks /insights) hits context 2
// without `__INITIAL_DATA__` set — there was no SSR run. The static getters
// then return the build-time markdown bundle, which goes stale the moment
// admin publishes a new article. To keep the page in sync without a hard
// refresh, components use the `useArticles` / `useArticle` hooks below:
// they render with whatever the static getters return for first paint, then
// fetch /api/insights/articles* on mount and swap in fresh data.
//
// The exported static API (getAllArticles, getArticleBySlug, etc.) is
// unchanged — SSR and prerender still call them, and they're used as the
// initial state inside the hooks.

import { useEffect, useState } from "react";

export interface Article {
  title: string;
  slug: string;
  category: string;
  date: string;
  readingTime: string;
  author: string;
  excerpt: string;
  thumbnail: string;
  body: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  Product: "text-emerald-500 border-emerald-500/30",
  Company: "text-purple-500 border-purple-500/30",
  Announcement: "text-amber-500 border-amber-500/30",
  Engineering: "text-sky-500 border-sky-500/30",
};

export function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] ?? "text-[var(--color-fg-tertiary)] border-[var(--color-border)]";
}

// Module-level state. SSR sets via setArticles(); client reads from
// window.__INITIAL_DATA__ on first access.
let articles: Article[] | null = null;

/** Called by Pages Functions before render(); seeds the article list. */
export function setArticles(rows: Article[]): void {
  articles = rows.slice().sort((a, b) => (a.date > b.date ? -1 : 1));
}

declare global {
  interface Window {
    __INITIAL_DATA__?: { articles?: Article[] };
  }
}

function loadFromHydration(): Article[] | null {
  if (typeof window === "undefined") return null;
  const data = window.__INITIAL_DATA__?.articles;
  if (!data) return null;
  return data.slice().sort((a, b) => (a.date > b.date ? -1 : 1));
}

// Vite-only fallback. The glob is ignored in production SSR (no markdown
// files at edge runtime), but in local dev/build it provides offline content
// so the rest of the site works without a D1 binding.
function loadFromGlob(): Article[] {
  const modules = import.meta.glob("/src/content/insights/*.md", {
    query: "?raw",
    import: "default",
    eager: true,
  }) as Record<string, string>;
  return Object.values(modules)
    .map((raw) => {
      const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
      if (!match) return null;
      const meta: Record<string, string> = {};
      for (const line of match[1]!.split("\n")) {
        const idx = line.indexOf(":");
        if (idx > 0) {
          const key = line.slice(0, idx).trim();
          let value = line.slice(idx + 1).trim();
          if (value.startsWith('"') && value.endsWith('"')) value = value.slice(1, -1);
          meta[key] = value;
        }
      }
      return {
        title: meta.title ?? "",
        slug: meta.slug ?? "",
        category: meta.category ?? "",
        date: meta.date ?? "",
        readingTime: meta.readingTime ?? "",
        author: meta.author ?? "",
        excerpt: meta.excerpt ?? "",
        thumbnail: meta.thumbnail ?? "",
        body: match[2]!.trim(),
      } satisfies Article;
    })
    .filter((a): a is Article => !!a && !!a.slug)
    .sort((a, b) => (a.date > b.date ? -1 : 1));
}

function ensureLoaded(): Article[] {
  if (articles) return articles;
  const fromHydration = loadFromHydration();
  if (fromHydration) {
    articles = fromHydration;
    return articles;
  }
  // Fallback (Vite dev / non-SSR build) — read from bundled markdown.
  articles = loadFromGlob();
  return articles;
}

export function getAllArticles(): Article[] {
  return ensureLoaded();
}

export function getArticleBySlug(slug: string): Article | undefined {
  return ensureLoaded().find((a) => a.slug === slug);
}

export function getArticlesByCategory(category: string): Article[] {
  const all = ensureLoaded();
  if (category === "All") return all;
  return all.filter((a) => a.category === category);
}

export function getRecentArticles(count: number): Article[] {
  return ensureLoaded().slice(0, count);
}

// Client-side hooks that pair the static getters (used for SSR / first paint)
// with a fetch from the public read API on mount. The fetch ensures the page
// reflects current D1 state even when:
//   - the user SPA-navigated in (no __INITIAL_DATA__ on this page load), or
//   - edge cache served stale SSR HTML to a colo that hadn't been purged.
//
// These hooks are no-ops on the server (useEffect doesn't run during
// renderToString), so SSR output and SEO are unaffected.

async function fetchArticles(): Promise<Article[] | null> {
  try {
    const res = await fetch("/api/insights/articles", { cache: "no-store" });
    if (!res.ok) return null;
    const data = (await res.json()) as { articles?: Article[] };
    return data.articles ?? null;
  } catch {
    return null;
  }
}

async function fetchArticle(slug: string): Promise<Article | null> {
  try {
    const res = await fetch(`/api/insights/articles/${encodeURIComponent(slug)}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { article?: Article };
    return data.article ?? null;
  } catch {
    return null;
  }
}

export function useArticles(): Article[] {
  const [list, setList] = useState<Article[]>(() => getAllArticles());
  useEffect(() => {
    let alive = true;
    fetchArticles().then((fresh) => {
      if (!alive || !fresh) return;
      // Update both component state and the module-level cache so subsequent
      // SPA navigations within this session read the fresh list.
      articles = fresh.slice().sort((a, b) => (a.date > b.date ? -1 : 1));
      setList(articles);
    });
    return () => { alive = false; };
  }, []);
  return list;
}

export function useArticle(slug: string | undefined): {
  article: Article | undefined;
  loading: boolean;
} {
  const [article, setArticle] = useState<Article | undefined>(() =>
    slug ? getArticleBySlug(slug) : undefined
  );
  // Only true while the API call is in flight AND we don't yet have anything
  // to show (no SSR seed, no glob fallback hit). Components can use this to
  // avoid an instant <Navigate to="/insights" /> redirect for newly-published
  // articles that aren't in the build-time bundle.
  const [loading, setLoading] = useState(() => !!slug && !getArticleBySlug(slug));
  useEffect(() => {
    if (!slug) return;
    let alive = true;
    fetchArticle(slug).then((fresh) => {
      if (!alive) return;
      if (fresh) setArticle(fresh);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [slug]);
  return { article, loading };
}
